if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Subscribe to variant changes to update BOGO data
        if (this.dataset.mainProductForm !== undefined) {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
          }
        }
      }
      
      handleVariantChange(event) {
        if (!event.data.variant) return;
        const variant = event.data.variant;
        
        // Check if this is a BOGO product (tag check from product-info)
        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        let isBogo = false;
        if (productInfo && productInfo.dataset.productTags) {
          const tags = productInfo.dataset.productTags.split(',').map(tag => tag.trim().toLowerCase());
          if (tags.includes('bogo')) {
            isBogo = true;
          }
        }
        
        if (!isBogo) {
          this.removeAttribute('data-is-bogo');
          return;
        }

        // It is BOGO, update attributes
        this.dataset.isBogo = 'true';
        
        // Update variant inventory from ProductVariantData
        const productId = productInfo ? productInfo.dataset.productId : null;
        let variantInventory = null;
        
        if (productId) {
           try {
            const variantDataScript = document.querySelector(`#ProductVariantData-${productId}`);
            if (variantDataScript) {
              const variantData = JSON.parse(variantDataScript.textContent);
              const variantItem = variantData.variants?.find(v => String(v.id) === String(variant.id));
              if (variantItem) {
                // If inventory is NOT tracked, treat as unlimited
                const isTracked = variantItem.inventory_management === 'shopify' && variantItem.inventory_policy !== 'continue';
                variantInventory = isTracked ? variantItem.inventory_quantity : 9999;
              }
            }
          } catch (e) {
            console.warn('Could not get BOGO variant inventory on change:', e);
          }
        }
        
        if (variantInventory !== null) {
          this.dataset.bogoVariantInventory = variantInventory;
          const bogoMaxQty = Math.floor(variantInventory / 2);
          this.dataset.bogoMaxQty = bogoMaxQty;
          
          // Fetch current cart to get fresh counts for this new variant
          fetch('/cart.js')
            .then(r => r.json())
            .then(cartData => {
               let cartQtyForVariant = 0;
               if (cartData.items) {
                 cartData.items.forEach(item => {
                   if (String(item.variant_id) === String(variant.id)) {
                     cartQtyForVariant += item.quantity;
                   }
                 });
               }
               
               const bogoCartPairs = Math.floor(cartQtyForVariant / 2);
               const bogoRemaining = Math.max(0, bogoMaxQty - bogoCartPairs);
               
               this.dataset.bogoCartQty = bogoCartPairs;
               this.dataset.bogoRemaining = bogoRemaining;
            })
            .catch(e => console.error('Error fetching cart for BOGO update:', e));
        } else {
           // Fallback if inventory not found (unlikely)
           this.removeAttribute('data-bogo-variant-inventory');
           this.removeAttribute('data-bogo-max-qty');
           this.removeAttribute('data-bogo-remaining');
        }
      }

      resolveMainFormQuantity(formData) {
        let currentQuantity = parseInt(formData.get('quantity') || '1', 10);
        const isMainProductForm = this.hasAttribute('data-main-product-form');

        if (!isMainProductForm) {
          return { quantity: currentQuantity, blocked: false };
        }

        const productInfo = this.closest('product-info') || document.querySelector('product-info');
        if (!productInfo || !productInfo.dataset.productTags) {
          return { quantity: currentQuantity, blocked: false };
        }

        const tags = productInfo.dataset.productTags.split(',').map(tag => tag.trim().toLowerCase());
        if (!tags.includes('bogo')) {
          return { quantity: currentQuantity, blocked: false };
        }

        const isBogo = this.dataset.isBogo === 'true';
        if (isBogo) {
          const bogoRemaining = parseInt(this.dataset.bogoRemaining || '0', 10);
          const bogoMaxQty = parseInt(this.dataset.bogoMaxQty || '0', 10);

          if (bogoMaxQty <= 0) {
            this.handleErrorMessage('Sorry, this BOGO item is currently out of stock.');
            return { quantity: currentQuantity, blocked: true };
          }

          if (currentQuantity > bogoRemaining) {
            if (bogoRemaining <= 0) {
              this.handleErrorMessage(window.cartStrings.maxQuantityError);
              return { quantity: currentQuantity, blocked: true };
            }
            this.handleErrorMessage(`You requested ${currentQuantity} items, but only ${bogoRemaining} pairs were available.`);
            currentQuantity = bogoRemaining;
          }
        }

        return { quantity: currentQuantity * 2, blocked: false };
      }

      buildLayeringUpsellFormData(baseFormData, baseVariantId, quantity, lineProperties) {
        const layeringUpsell = document.querySelector('layering-upsell');
        const upsellVariantId = layeringUpsell && typeof layeringUpsell.getSelectedVariantId === 'function'
          ? layeringUpsell.getSelectedVariantId()
          : '';

        if (!upsellVariantId) {
          return null;
        }

        const formData = new FormData();
        const props = lineProperties || {};

        formData.append('items[0][id]', String(baseVariantId));
        formData.append('items[0][quantity]', String(quantity));
        Object.keys(props).forEach((key) => {
          if (props[key] !== '' && props[key] != null) {
            formData.append(`items[0][properties][${key}]`, String(props[key]));
          }
        });

        formData.append('items[1][id]', String(upsellVariantId));
        formData.append('items[1][quantity]', String(quantity));
        Object.keys(props).forEach((key) => {
          if (props[key] !== '' && props[key] != null) {
            formData.append(`items[1][properties][${key}]`, String(props[key]));
          }
        });

        return formData;
      }

      getLayeringLineProperties() {
        const layeringUpsell = document.querySelector('layering-upsell');
        if (layeringUpsell && typeof layeringUpsell.getLineItemProperties === 'function') {
          return layeringUpsell.getLineItemProperties();
        }

        const soldAsHidden = document.getElementById('SoldAsHiddenInput');
        const unitsHidden = document.getElementById('UnitsPerHiddenInput');
        const twoPackHidden = document.getElementById('TwoPackHiddenInput');

        return {
          'SOLD AS': soldAsHidden ? soldAsHidden.value : '',
          '_units_per': unitsHidden ? unitsHidden.value : '1',
          '_2pack': twoPackHidden ? twoPackHidden.value : '',
        };
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const baseFormData = new FormData(this.form);
        const quantityResult = this.resolveMainFormQuantity(baseFormData);

        if (quantityResult.blocked) {
          this.submitButton.setAttribute('aria-disabled', false);
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const isMainProductForm = this.hasAttribute('data-main-product-form');
        const baseVariantId = baseFormData.get('id');
        let formData = baseFormData;

        if (isMainProductForm) {
          const layeringFormData = this.buildLayeringUpsellFormData(
            baseFormData,
            baseVariantId,
            quantityResult.quantity,
            this.getLayeringLineProperties()
          );

          if (layeringFormData) {
            formData = layeringFormData;
          } else {
            formData.set('quantity', String(quantityResult.quantity));
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const submittedVariantId = formData.get('id') || (formData.get('items[0][id]') || baseVariantId);

        fetch(`${routes.cart_add_url}`, config)
          //.then((response) => response.json())
            .then(async (response) => {
                let data = {};

                try {
                    data = await response.json();
                } catch (e) {
                    console.warn('Invalid JSON from cart_add:', e);
                    data = {};
                }
                return data;
            })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: submittedVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              

              // Check if this is a "were added" message and replace with cleaner message
              let errorMessage = response.description;
              if (errorMessage && (errorMessage.includes('were added') || errorMessage.includes('maximum quantity') 
                || errorMessage.includes('out of stock'))) {
                errorMessage = window.cartStrings.maxQuantityError;
              }
              this.handleErrorMessage(errorMessage);

              // Check if this is a partial add (422 with items added) or complete failure
              // If response.sections doesn't exist, fetch the cart to check if items were added
              if (!response.sections) {
                // Check if message indicates partial add (e.g., "Only X items were added")
                const isPartialAdd = response.description && 
                                    (response.description.includes('were added') || 
                                     response.description.includes('items were added') || 
                                     response.description.includes('availability') ||
                                     response.description.includes('sold out'));
                
                // Check if the error is due to sold out
                const isSoldOut = response.description && 
                                  response.description.toLowerCase().includes('sold out');
                
                if (isSoldOut) {
                  // Add is-empty class to cart-drawer for sold out items
                  setTimeout(() => {
                    const cartDrawer = document.querySelector('cart-drawer');
                    const cartDrawerItems = document.querySelector('cart-drawer-items');
                    
                    // Only add is-empty class to cart-drawer if cart-drawer-items has is-empty class
                    if (cartDrawer && cartDrawerItems && cartDrawerItems.classList.contains('is-empty')) {
                      cartDrawer.classList.add('is-empty');
                    }  
                  }, 1500);
                }
                
                if (isPartialAdd && this.cart) {
                  // Items were partially added - fetch updated cart sections
                  const sectionsToRender = this.cart.getSectionsToRender();
                  const sectionsParam = sectionsToRender.map((section) => section.id).join(',');
                  
                  try {
                    const cartResponse = await fetch(`${window.location.pathname}?sections=${sectionsParam}`);
                    response.sections = await cartResponse.json();
                  } catch (e) {
                    console.error('Failed to fetch cart sections:', e);
                    this.error = true;
                    return;
                  }
                } else {
                  this.error = true;
                  return;
                }
              }
              // Items were added (either normally or partially), continue to update cart
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: submittedVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            
            // Update BOGO data attributes after successful add
            this.updateBogoDataAfterAdd(submittedVariantId);
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }
      
      updateBogoDataAfterAdd(variantId) {
        // Check if this form has BOGO data
        const isBogo = this.dataset.isBogo === 'true';
        if (!isBogo) return;

        fetch('/cart.js')
          .then(response => response.json())
          .then(cartData => {
            // Calculate new cart quantity for this variant
            let cartQtyForVariant = 0;
            if (cartData.items) {
              cartData.items.forEach(item => {
                if (String(item.variant_id) === String(variantId)) {
                  cartQtyForVariant += item.quantity;
                }
              });
            }

            // For BOGO: cart qty is doubled (PAID + FREE), so divide by 2
            const bogoCartPairs = Math.floor(cartQtyForVariant / 2);

            // Get max qty (this doesn't change, it's based on inventory)
            const bogoMaxQty = parseInt(this.dataset.bogoMaxQty || '0', 10);

            // Calculate new remaining
            const bogoRemaining = Math.max(0, bogoMaxQty - bogoCartPairs);

            // Update the data attributes
            this.dataset.bogoCartQty = bogoCartPairs;
            this.dataset.bogoRemaining = bogoRemaining;
          })
          .catch(e => {
            console.error('Error updating BOGO data:', e);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
